import { CADMarker } from '../../../types/cad';

// Matches [point:...], [pointA:...], [pointB:...] markers (capture group 1 = A|B|undefined)
// eslint-disable-next-line no-useless-escape
const MARKER_REGEX = /\[point([AB])?:(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?):([^:\[\]]+)(?::(info|warning|error))?\]/g;

/**
 * Parse [point:x,y,z:label:type] markers from LLM response text.
 * Only matches complete tags (closed bracket). Deduplicates. Caps at 5.
 */
export function parseMarkers(text: string): CADMarker[] {
  const regex = new RegExp(MARKER_REGEX.source, 'g');
  const markers: CADMarker[] = [];
  const seen = new Set<string>();
  let match;
  while ((match = regex.exec(text)) !== null) {
    const side = match[1] as 'A' | 'B' | undefined;
    const x = parseFloat(match[2]), y = parseFloat(match[3]), z = parseFloat(match[4]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    const label = match[5].trim();
    if (!label) continue;
    const type = (match[6] as CADMarker['type']) || 'info';
    const key = `${x},${y},${z},${label},${type}`;
    if (seen.has(key)) continue;
    seen.add(key);
    markers.push({ position: [x, y, z], label, type, side });
  }
  return markers.slice(0, 5);
}

/**
 * Replace marker syntax with pin badge for display.
 */
export function stripMarkerSyntax(text: string): string {
  // Replace [point:...] with 📍label — reuse shared regex source
  return text.replace(new RegExp(MARKER_REGEX.source, 'g'), '📍$5');
}

export function markersEqual(a: CADMarker[], b: CADMarker[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((m, i) =>
    m.position[0] === b[i].position[0] &&
    m.position[1] === b[i].position[1] &&
    m.position[2] === b[i].position[2] &&
    m.label === b[i].label &&
    m.type === b[i].type &&
    m.side === b[i].side,
  );
}
